// Social Media Studio: KI-Texte (A/B) · KI-Bilder · Bildbearbeitung · Voice-Over · Plattform-Vorschauen · Veröffentlichen
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { CanvaPanel } from "./CanvaPanel";
import {
  Clapperboard, Wand2, ImagePlus, Upload, Mic2, Play, Pause, Send, Loader2, Sparkles, Heart,
  MessageCircle, Share2, Bookmark, ThumbsUp, Music2
} from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api`;
const STUDIO_PLATFORMS = ["instagram", "tiktok", "youtube", "facebook", "whatsapp", "telegram", "google_business", "pinterest", "linkedin", "x", "facebook_marketplace", "ebay", "etsy"];
const VOICES = ["alloy", "nova", "shimmer", "echo", "onyx", "fable"];
const DEFAULT_EDIT = { brightness: 100, contrast: 100, saturate: 110, warmth: 0, overlay: "", pos: "bottom", size: 28 };

const cssFilter = (e) => `brightness(${e.brightness}%) contrast(${e.contrast}%) saturate(${e.saturate}%) sepia(${e.warmth}%)`;
const mediaSrc = (u) => (u?.startsWith("/api") ? `${BASE}${u}` : u);

export function SocialStudio() {
  const [props, setProps] = useState([]);
  const [platformsMeta, setPlatformsMeta] = useState({});
  const [brief, setBrief] = useState({ topic: "", property_id: "", tone: "warm", language: "de" });
  const [selected, setSelected] = useState(["instagram", "tiktok", "youtube", "facebook", "whatsapp"]);
  const [texts, setTexts] = useState({});
  const [variant, setVariant] = useState("a");
  const [busy, setBusy] = useState("");
  const [image, setImage] = useState(null);
  const [edit, setEdit] = useState(DEFAULT_EDIT);
  const [audio, setAudio] = useState(null);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    axios.get(`${API}/properties`).then((r) => {
      setProps(r.data);
      if (r.data[0]) {
        setBrief((b) => ({ ...b, property_id: r.data[0].id, topic: `Herbstangebot im ${r.data[0].name}` }));
        setImage({ url: r.data[0].images?.[0] });
      }
    });
    axios.get(`${API}/marketing/platforms`).then((r) => setPlatformsMeta(Object.fromEntries(r.data.map((p) => [p.key, p]))));
  }, []);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 4000); };

  const generateCopy = async () => {
    setBusy("copy");
    try {
      const r = await axios.post(`${API}/marketing/ai/copy`, { ...brief, platforms: selected });
      setTexts(r.data.variants);
    } catch (e) { flash(`Fehler: ${e.response?.data?.detail || e.message}`); }
    finally { setBusy(""); }
  };

  const setText = (p, v, val) => setTexts((t) => ({ ...t, [p]: { ...(t[p] || { a: "", b: "" }), [v]: val } }));

  const improve = async (p) => {
    setBusy(`imp-${p}`);
    try {
      const r = await axios.post(`${API}/marketing/ai/improve`, { text: texts[p]?.[variant] || brief.topic, platform: p,
        instruction: "Mach den Text ansprechender, buchungsstärker und plattformgerecht" });
      setText(p, variant, r.data.text);
    } finally { setBusy(""); }
  };

  const publish = async (kind, budget, when) => {
    const plats = selected.filter((p) => texts[p]?.a);
    if (!plats.length) { flash("Bitte zuerst Texte erzeugen oder schreiben"); return; }
    setBusy("publish");
    try {
      const r = await axios.post(`${API}/marketing/posts`, {
        title: brief.topic || "Studio-Beitrag", platforms: plats,
        texts: Object.fromEntries(plats.map((p) => [p, texts[p].a])),
        variant_b: Object.fromEntries(plats.filter((p) => texts[p].b).map((p) => [p, texts[p].b])),
        media_ids: image?.id ? [image.id] : [], audio_id: audio?.id || null,
        kind, budget_eur: parseFloat(budget || 0), scheduled_at: when || null,
      });
      const pend = r.data.queued.filter((q) => q.status === "pending").length;
      flash(`✓ ${r.data.queued.length} Aktionen erstellt · ${pend} warten auf Freigabe im Marketing-Hub`);
    } finally { setBusy(""); }
  };

  return (
    <div className="space-y-5" data-testid="social-studio">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-bold text-2xl flex items-center gap-2">
            <Clapperboard size={22} className="text-eb-primary" /> Social Media Studio
          </h2>
          <p className="text-eb-muted text-sm">Text · Bild · Ton · Reel-Vorschau – KI-Modelle automatisch gewählt (DeutschlandGPT/Claude für Text, Nano Banana für Bild, OpenAI TTS für Sprache)</p>
        </div>
        {msg && <span className="text-emerald-400 text-xs max-w-sm" data-testid="studio-msg">{msg}</span>}
        <AiStatus />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[420px_1fr] gap-5">
        <div className="space-y-4">
          <BriefPanel brief={brief} setBrief={setBrief} props={props} selected={selected} setSelected={setSelected}
            meta={platformsMeta} onGenerate={generateCopy} busy={busy === "copy"} />
          <ImagePanel image={image} setImage={setImage} edit={edit} setEdit={setEdit} brief={brief} flash={flash} />
          <CanvaPanel flash={flash} compact onImported={(m) => { if (m.mime !== "application/pdf") setImage({ id: m.id, url: m.url, mime: m.mime }); }} />
          <AudioPanel audio={audio} setAudio={setAudio} texts={texts} selected={selected} flash={flash} />
        </div>
        <div className="space-y-4">
          <TextEditor selected={selected} texts={texts} variant={variant} setVariant={setVariant}
            setText={setText} improve={improve} busy={busy} meta={platformsMeta} />
          <PreviewGrid selected={selected} texts={texts} variant={variant} image={image} edit={edit} audio={audio} meta={platformsMeta} />
          <PublishBar onPublish={publish} busy={busy === "publish"} />
        </div>
      </div>
    </div>
  );
}

function AiStatus() {
  const [s, setS] = useState(null);
  useEffect(() => { axios.get(`${API}/marketing/ai/status`).then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const warn = s.ok === false;
  return (
    <span className={`channel-pill ${warn ? "bg-amber-500/15 text-amber-300" : "bg-emerald-500/15 text-emerald-300"}`}
      title={s.last_error || ""} data-testid="ai-status-badge">
      {s.provider} · {s.model} · {warn ? "kein Guthaben → Fallback Claude" : s.ok ? "aktiv" : "bereit"}
    </span>
  );
}

function BriefPanel({ brief, setBrief, props, selected, setSelected, meta, onGenerate, busy }) {
  const toggle = (p) => setSelected((s) => (s.includes(p) ? s.filter((x) => x !== p) : [...s, p]));
  return (
    <div className="card space-y-2" data-testid="studio-brief">
      <div className="overline">1 · Briefing</div>
      <select value={brief.property_id} onChange={(e) => setBrief({ ...brief, property_id: e.target.value })} data-testid="studio-property">
        {props.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <textarea rows={2} value={brief.topic} placeholder="Thema / Angebot" data-testid="studio-topic"
        onChange={(e) => setBrief({ ...brief, topic: e.target.value })} />
      <div className="grid grid-cols-2 gap-2">
        <select value={brief.tone} onChange={(e) => setBrief({ ...brief, tone: e.target.value })} data-testid="studio-tone">
          {["warm", "luxuriös", "dringlich", "verspielt", "seriös"].map((t) => <option key={t}>{t}</option>)}
        </select>
        <select value={brief.language} onChange={(e) => setBrief({ ...brief, language: e.target.value })} data-testid="studio-language">
          {["de", "en", "fr", "es", "it", "nl"].map((l) => <option key={l}>{l}</option>)}
        </select>
      </div>
      <div className="flex flex-wrap gap-1">
        {STUDIO_PLATFORMS.map((p) => (
          <button key={p} onClick={() => toggle(p)} data-testid={`studio-platform-${p}`}
            className={`px-2 py-0.5 rounded-full text-[11px] border transition-colors ${selected.includes(p) ? "border-eb-primary text-eb-primary bg-eb-primary/10" : "border-white/10 text-eb-muted"}`}>
            {meta[p]?.label || p}
          </button>
        ))}
      </div>
      <button className="btn btn-primary w-full justify-center" onClick={onGenerate} disabled={busy} data-testid="studio-generate-copy">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} KI-Texte A/B für alle Kanäle
      </button>
    </div>
  );
}

function ImagePanel({ image, setImage, edit, setEdit, brief, flash }) {
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState("");
  const [gallery, setGallery] = useState([]);
  const fileRef = useRef();
  const loadGallery = () => axios.get(`${API}/marketing/media`).then((r) => setGallery(r.data.filter((m) => m.kind === "image")));
  useEffect(() => { loadGallery(); }, []);

  const generate = async () => {
    setBusy("gen");
    try {
      const r = await axios.post(`${API}/marketing/studio/image`, { prompt: prompt || brief.topic, format: "square" });
      setImage(r.data); setEdit(DEFAULT_EDIT); loadGallery();
    } catch (e) { flash(`Bildfehler: ${e.response?.data?.detail || e.message}`); }
    finally { setBusy(""); }
  };

  const upload = (f) => {
    const reader = new FileReader();
    reader.onload = async () => {
      const [head, data] = reader.result.split(",");
      const r = await axios.post(`${API}/marketing/media`, { data_b64: data, mime: head.split(":")[1].split(";")[0] });
      setImage(r.data); loadGallery();
    };
    reader.readAsDataURL(f);
  };

  const bake = async () => {
    setBusy("bake");
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = mediaSrc(image.url);
      await img.decode();
      const c = document.createElement("canvas");
      c.width = img.naturalWidth; c.height = img.naturalHeight;
      const ctx = c.getContext("2d");
      ctx.filter = cssFilter(edit);
      ctx.drawImage(img, 0, 0);
      if (edit.overlay) {
        ctx.filter = "none";
        const fs = Math.round(c.width * edit.size / 400);
        ctx.font = `bold ${fs}px Outfit, sans-serif`;
        ctx.textAlign = "center"; ctx.fillStyle = "#fff"; ctx.shadowColor = "rgba(0,0,0,.6)"; ctx.shadowBlur = fs / 3;
        const y = edit.pos === "top" ? fs * 1.6 : edit.pos === "center" ? c.height / 2 : c.height - fs;
        ctx.fillText(edit.overlay, c.width / 2, y);
      }
      const data = c.toDataURL("image/png").split(",")[1];
      const r = await axios.post(`${API}/marketing/media`, { data_b64: data, mime: "image/png" });
      setImage(r.data); setEdit(DEFAULT_EDIT); loadGallery();
      flash("✓ Bearbeitetes Bild gespeichert");
    } catch (e) { flash("Bild konnte nicht gerendert werden (CORS) – bitte KI-Bild oder Upload nutzen"); }
    finally { setBusy(""); }
  };

  const slider = (k, label, min, max) => (
    <div>
      <div className="text-[10px] text-eb-muted flex justify-between"><span>{label}</span><span>{edit[k]}</span></div>
      <input type="range" min={min} max={max} value={edit[k]} style={{ padding: 0 }} data-testid={`edit-${k}`}
        onChange={(e) => setEdit({ ...edit, [k]: parseInt(e.target.value) })} />
    </div>
  );

  return (
    <div className="card space-y-2" data-testid="studio-image-panel">
      <div className="overline">2 · Bild &amp; Bearbeitung</div>
      <div className="flex gap-1">
        <input value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Bild-Prompt (leer = Thema)" data-testid="image-prompt" />
        <button className="btn !px-2" onClick={generate} disabled={busy === "gen"} data-testid="image-generate-btn" title="KI-Bild">
          {busy === "gen" ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
        </button>
        <button className="btn !px-2" onClick={() => fileRef.current.click()} data-testid="image-upload-btn" title="Hochladen"><Upload size={14} /></button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
      </div>
      {gallery.length > 0 && (
        <div className="flex gap-1 overflow-x-auto pb-1">
          {gallery.slice(0, 12).map((g) => (
            <img key={g.id} src={mediaSrc(g.url)} alt="" onClick={() => setImage(g)} data-testid={`gallery-${g.id}`}
              className={`w-12 h-12 object-cover rounded cursor-pointer border ${image?.id === g.id ? "border-eb-primary" : "border-transparent"}`} />
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-x-3 gap-y-1">
        {slider("brightness", "Helligkeit", 50, 150)}{slider("contrast", "Kontrast", 50, 150)}
        {slider("saturate", "Sättigung", 0, 200)}{slider("warmth", "Wärme", 0, 60)}
      </div>
      <div className="grid grid-cols-[1fr_90px] gap-1">
        <input value={edit.overlay} placeholder="Text im Bild (z. B. −20 % Last-Minute)" data-testid="edit-overlay"
          onChange={(e) => setEdit({ ...edit, overlay: e.target.value })} />
        <select value={edit.pos} onChange={(e) => setEdit({ ...edit, pos: e.target.value })} data-testid="edit-pos">
          <option value="top">oben</option><option value="center">Mitte</option><option value="bottom">unten</option>
        </select>
      </div>
      <button className="btn w-full justify-center" onClick={bake} disabled={!image?.url || busy === "bake"} data-testid="edit-apply-btn">
        {busy === "bake" ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Bearbeitung übernehmen
      </button>
    </div>
  );
}

function AudioPanel({ audio, setAudio, texts, selected, flash }) {
  const [text, setText] = useState("");
  const [voice, setVoice] = useState("nova");
  const [speed, setSpeed] = useState(1);
  const [busy, setBusy] = useState(false);
  const fromCopy = () => setText((texts.tiktok?.a || texts[selected[0]]?.a || "").replace(/#\S+/g, "").slice(0, 600));

  const gen = async () => {
    if (!text) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/agents/tts`, { text, voice, speed }, { responseType: "blob" });
      const reader = new FileReader();
      reader.onload = async () => {
        const up = await axios.post(`${API}/marketing/media`, { data_b64: reader.result.split(",")[1], mime: "audio/mpeg", kind: "audio" });
        setAudio({ ...up.data, src: URL.createObjectURL(r.data), volume: 0.9 });
      };
      reader.readAsDataURL(r.data);
    } catch (e) { flash("Voice-Over fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  return (
    <div className="card space-y-2" data-testid="studio-audio-panel">
      <div className="flex items-center justify-between">
        <div className="overline">3 · Ton / Voice-Over</div>
        <button className="text-[11px] text-eb-primary" onClick={fromCopy} data-testid="audio-from-copy">aus Text übernehmen</button>
      </div>
      <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Sprechertext für Reel / Short" data-testid="audio-text" />
      <div className="grid grid-cols-2 gap-2">
        <select value={voice} onChange={(e) => setVoice(e.target.value)} data-testid="audio-voice">
          {VOICES.map((v) => <option key={v}>{v}</option>)}
        </select>
        <div>
          <div className="text-[10px] text-eb-muted">Tempo {speed}×</div>
          <input type="range" min="0.75" max="1.5" step="0.05" value={speed} style={{ padding: 0 }}
            onChange={(e) => setSpeed(parseFloat(e.target.value))} data-testid="audio-speed" />
        </div>
      </div>
      <button className="btn w-full justify-center" onClick={gen} disabled={busy || !text} data-testid="audio-generate-btn">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Mic2 size={14} />} Voice-Over erzeugen
      </button>
      {audio && (
        <div>
          <div className="text-[10px] text-eb-muted">Lautstärke {Math.round(audio.volume * 100)} %</div>
          <input type="range" min="0" max="1" step="0.05" value={audio.volume} style={{ padding: 0 }} data-testid="audio-volume"
            onChange={(e) => setAudio({ ...audio, volume: parseFloat(e.target.value) })} />
        </div>
      )}
    </div>
  );
}

function TextEditor({ selected, texts, variant, setVariant, setText, improve, busy, meta }) {
  return (
    <div className="card" data-testid="studio-text-editor">
      <div className="flex items-center justify-between mb-3">
        <div className="overline">4 · Texte je Kanal (manuell oder KI)</div>
        <div className="flex gap-1 eb-surface rounded-full p-1">
          {["a", "b"].map((v) => (
            <button key={v} onClick={() => setVariant(v)} data-testid={`variant-${v}`}
              className={`px-3 py-0.5 rounded-full text-xs font-semibold ${variant === v ? "bg-eb-primary text-[#0B0F17]" : "text-eb-muted"}`}>
              Variante {v.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {selected.map((p) => {
          const val = texts[p]?.[variant] || "";
          const max = meta[p]?.max_chars || 2000;
          return (
            <div key={p}>
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs font-semibold">{meta[p]?.label || p}</span>
                <span className="flex items-center gap-2">
                  <span className={`text-[10px] font-mono ${val.length > max ? "text-rose-400" : "text-eb-muted"}`}>{val.length}/{max}</span>
                  <button className="text-[11px] text-eb-primary flex items-center gap-0.5" onClick={() => improve(p)} data-testid={`improve-${p}`}>
                    {busy === `imp-${p}` ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />} KI verbessern
                  </button>
                </span>
              </div>
              <textarea rows={4} value={val} onChange={(e) => setText(p, variant, e.target.value)} data-testid={`text-${p}`} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- Previews ---------------- */
function Visual({ image, edit, ratio, kenburns }) {
  const posCls = { top: "top-3", center: "top-1/2 -translate-y-1/2", bottom: "bottom-3" }[edit.pos];
  return (
    <div className="relative overflow-hidden bg-black" style={{ aspectRatio: ratio }}>
      {image?.url && <img src={mediaSrc(image.url)} alt="" className={`w-full h-full object-cover ${kenburns ? "studio-kenburns" : ""}`} style={{ filter: cssFilter(edit) }} />}
      {edit.overlay && <div className={`absolute left-0 right-0 ${posCls} text-center font-heading font-bold text-white px-2`}
        style={{ fontSize: edit.size * 0.6, textShadow: "0 2px 8px rgba(0,0,0,.7)" }}>{edit.overlay}</div>}
    </div>
  );
}

function ReelPreview({ text, image, edit, audio, label }) {
  const [playing, setPlaying] = useState(false);
  const ref = useRef();
  const toggle = () => {
    if (!audio) { setPlaying(!playing); return; }
    if (playing) ref.current.pause(); else { ref.current.volume = audio.volume; ref.current.currentTime = 0; ref.current.play(); }
    setPlaying(!playing);
  };
  return (
    <div className="relative rounded-xl overflow-hidden border border-white/10 max-w-[230px] mx-auto">
      <Visual image={image} edit={edit} ratio="9/16" kenburns={playing} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />
      <button onClick={toggle} className="absolute top-2 right-2 bg-black/50 rounded-full p-1.5" data-testid={`reel-play-${label}`}>
        {playing ? <Pause size={14} /> : <Play size={14} />}
      </button>
      <div className="absolute right-2 bottom-16 flex flex-col gap-3 text-white"><Heart size={18} /><MessageCircle size={18} /><Share2 size={18} /></div>
      <div className="absolute bottom-2 left-2 right-10 text-[10px] text-white leading-snug line-clamp-4">
        <div className="font-bold">@sunworker.ferien</div>{text}
        <div className="flex items-center gap-1 mt-1 opacity-80"><Music2 size={10} /> {audio ? "Voice-Over · Original" : "Originalton"}</div>
      </div>
      {audio && <audio ref={ref} src={audio.src} onEnded={() => setPlaying(false)} />}
    </div>
  );
}

function PlatformPreview({ p, text, image, edit, audio, label }) {
  const fmt = { tiktok: "reel", pinterest: "reel", instagram: "square", youtube: "video", whatsapp: "chat", telegram: "chat",
    facebook_marketplace: "listing", ebay: "listing", etsy: "listing" }[p] || "feed";
  const head = (
    <div className="flex items-center gap-2 p-2">
      <div className="w-6 h-6 rounded-full bg-eb-primary" />
      <div className="text-[11px] font-semibold">Sunworker Ferienwelt <span className="text-eb-muted font-normal">· Gesponsert</span></div>
    </div>
  );
  let body;
  if (fmt === "reel") body = <ReelPreview text={text} image={image} edit={edit} audio={audio} label={p} />;
  else if (fmt === "chat") body = (
    <div className="p-3 rounded-xl" style={{ background: p === "whatsapp" ? "#0b141a" : "#17212b" }}>
      <div className="max-w-[85%] ml-auto rounded-lg overflow-hidden" style={{ background: p === "whatsapp" ? "#005c4b" : "#2b5278" }}>
        <Visual image={image} edit={edit} ratio="4/3" />
        <div className="p-2 text-[11px] whitespace-pre-line line-clamp-6">{text}</div>
        <div className="text-[9px] text-right px-2 pb-1 opacity-60">09:41 ✓✓</div>
      </div>
    </div>
  );
  else if (fmt === "video") body = (
    <div className="rounded-lg overflow-hidden border border-white/10">
      <div className="relative"><Visual image={image} edit={edit} ratio="16/9" />
        <div className="absolute inset-0 flex items-center justify-center"><div className="bg-red-600 rounded-xl px-4 py-2"><Play size={16} fill="white" /></div></div></div>
      <div className="p-2 text-[11px] font-semibold line-clamp-2">{text.split("\n")[0]}</div>
      <div className="px-2 pb-2 text-[10px] text-eb-muted">Sunworker Ferienwelt · 1,2 Tsd. Aufrufe</div>
    </div>
  );
  else if (fmt === "listing") body = (
    <div className="rounded-lg overflow-hidden border border-white/10 bg-white text-black">
      <Visual image={image} edit={edit} ratio="4/3" />
      <div className="p-2"><div className="font-bold text-sm">ab € {text.match(/(\d{2,4})\s?€/)?.[1] || "—"} / Nacht</div>
        <div className="text-[11px] line-clamp-3">{text}</div><div className="text-[10px] text-gray-500 mt-1">Tirol, AT · Privat/Gewerblich</div></div>
    </div>
  );
  else body = (
    <div className="rounded-lg overflow-hidden border border-white/10 bg-[#18191a]">
      {head}<div className="px-2 pb-2 text-[11px] whitespace-pre-line line-clamp-5">{text}</div>
      <Visual image={image} edit={edit} ratio={fmt === "square" ? "1/1" : "4/5"} />
      <div className="flex justify-around p-2 text-eb-muted text-[10px]">
        <span className="flex items-center gap-1"><ThumbsUp size={11} /> Gefällt mir</span>
        <span className="flex items-center gap-1"><MessageCircle size={11} /> Kommentar</span>
        <span className="flex items-center gap-1">{p === "instagram" ? <Bookmark size={11} /> : <Share2 size={11} />} Teilen</span>
      </div>
    </div>
  );
  return (
    <div data-testid={`preview-${p}`}>
      <div className="text-[11px] font-mono text-eb-muted mb-1 uppercase">{label}</div>
      {body}
    </div>
  );
}

function PreviewGrid({ selected, texts, variant, image, edit, audio, meta }) {
  return (
    <div className="card" data-testid="studio-previews">
      <div className="overline mb-3">5 · Live-Vorschau in den Original-Oberflächen (Variante {variant.toUpperCase()})</div>
      <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4">
        {selected.map((p) => (
          <PlatformPreview key={p} p={p} text={texts[p]?.[variant] || "Text erscheint hier …"} image={image}
            edit={edit} audio={audio} label={meta[p]?.label || p} />
        ))}
      </div>
    </div>
  );
}

function PublishBar({ onPublish, busy }) {
  const [kind, setKind] = useState("post");
  const [budget, setBudget] = useState(30);
  const [when, setWhen] = useState("");
  return (
    <div className="card flex flex-wrap items-end gap-3" data-testid="studio-publish-bar">
      <div>
        <div className="overline mb-1">Art</div>
        <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="publish-kind">
          <option value="post">Organischer Beitrag</option><option value="ad">Bezahlte Anzeige</option>
        </select>
      </div>
      {kind === "ad" && (
        <div className="w-28">
          <div className="overline mb-1">Budget €</div>
          <input type="number" value={budget} onChange={(e) => setBudget(e.target.value)} data-testid="publish-budget" />
        </div>
      )}
      <div>
        <div className="overline mb-1">Zeitpunkt</div>
        <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="publish-when" />
      </div>
      <button className="btn btn-primary ml-auto" onClick={() => onPublish(kind, budget, when)} disabled={busy} data-testid="publish-btn">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} An Manager-Agent zur Freigabe
      </button>
    </div>
  );
}
